/**
 * Import validation utilities for menu import functionality
 * Optimized for millisecond-level performance
 */

import { getTranslation } from './translate.js';

/**
 * Validates a single dish row from import data
 * @param {Object} row - The row data to validate
 * @param {number} rowIndex - The row index (0-based)
 * @param {Object} context - Validation context (existing dishes, tags, cuisines)
 * @param {string} language - Current language
 * @returns {Object} Validation result with isValid, errors, warnings
 */
export const validateDishRow = (row, rowIndex, context, language) => {
  const errors = [];
  const warnings = [];
  
  // Required fields validation (fast checks first)
  // Handle both "nameHy" and "name" (from export format)
  const nameHy = row.nameHy || row.name || '';
  if (!nameHy || nameHy.trim() === '') {
    errors.push({
      field: 'nameHy',
      message: getTranslation('import.importErrorMessages.nameHyRequired', language) || 'Name (Armenian) is required'
    });
  }

  // Handle type from multiple possible field names
  const typeValue = row.type || row.typeAm || row.typeEn || row.typeRu || '';
  let validatedType = '';
  if (!typeValue || typeValue.trim() === '') {
    errors.push({
      field: 'type',
      message: getTranslation('import.importErrorMessages.typeRequired', language) || 'Type is required'
    });
  } else {
    // Validate type enum
    const validTypes = ['BREAKFAST', 'LUNCH', 'DINNER', 'SIDE', 'APPETIZER', 'DESSERT', 'SEMIFINISHED'];
    validatedType = typeValue.toUpperCase().trim();
    if (!validTypes.includes(validatedType)) {
      errors.push({
        field: 'type',
        message: getTranslation('import.importErrorMessages.invalidType', language) || `Invalid type. Must be one of: ${validTypes.join(', ')}`
      });
    }
  }

  if (!row.expirationDate || isNaN(Number(row.expirationDate))) {
    errors.push({
      field: 'expirationDate',
      message: getTranslation('import.importErrorMessages.expirationDateRequired', language) || 'Expiration Date is required and must be a number'
    });
  }

  // Ingredients validation - check multiple possible field names
  const ingredientHy = row.ingredientHy || row.ingredientsAm || row.ingredientsHy || '';
  if (!ingredientHy || ingredientHy.trim() === '') {
    errors.push({
      field: 'ingredientHy',
      message: getTranslation('import.importErrorMessages.ingredientHyRequired', language) || 'Ingredients (Armenian) is required'
    });
  }

  // Dish Tag validation (required) - check multiple possible field names
  const tagValue = row.dishTagHy || row.dishCategoryAm || row.dishCategoryHy || 
                   row.dishTagEn || row.dishCategoryEn || 
                   row.dishTagRu || row.dishCategoryRu;
  
  if (!tagValue || tagValue.trim() === '') {
    errors.push({
      field: 'dishTagHy',
      message: getTranslation('import.importErrorMessages.dishTagRequired', language) || 'Dish Category is required'
    });
  } else if (context.tagsMap) {
    // Check if tag exists (O(1) lookup)
    const tagKey = tagValue.toLowerCase().trim();
    if (!context.tagsMap.has(tagKey)) {
      // Also check in tags array for translations
      let found = false;
      if (context.tags) {
        for (const tag of context.tags) {
          if (tag.tag && tag.tag.toLowerCase().trim() === tagKey) {
            found = true;
            break;
          }
          if (tag.translations) {
            for (const trans of tag.translations) {
              if (trans.value && trans.value.toLowerCase().trim() === tagKey) {
                found = true;
                break;
              }
            }
            if (found) break;
          }
        }
      }
      if (!found) {
        errors.push({
          field: 'dishTagHy',
          message: getTranslation('import.importErrorMessages.dishTagNotFound', language) || `Dish Category "${tagValue}" not found. Please use an existing category.`
        });
      }
    }
  }

  // Cuisine Tag validation (required) - check multiple possible field names
  const cuisineValue = row.cuisineHy || row.cuisineAm || 
                       row.cuisineEn || row.cuisineRu;
  
  if (!cuisineValue || cuisineValue.trim() === '') {
    errors.push({
      field: 'cuisineHy',
      message: getTranslation('import.importErrorMessages.cuisineRequired', language) || 'Cuisine is required'
    });
  } else if (context.cuisinesMap) {
    // Check if cuisine exists (O(1) lookup)
    const cuisineKey = cuisineValue.toLowerCase().trim();
    if (!context.cuisinesMap.has(cuisineKey)) {
      // Also check in cuisines array for translations
      let found = false;
      if (context.cuisines) {
        for (const cuisine of context.cuisines) {
          if (cuisine.cuisineTag && cuisine.cuisineTag.toLowerCase().trim() === cuisineKey) {
            found = true;
            break;
          }
          if (cuisine.translations) {
            for (const trans of cuisine.translations) {
              if (trans.value && trans.value.toLowerCase().trim() === cuisineKey) {
                found = true;
                break;
              }
            }
            if (found) break;
          }
        }
      }
      if (!found) {
        errors.push({
          field: 'cuisineHy',
          message: getTranslation('import.importErrorMessages.cuisineNotFound', language) || `Cuisine "${cuisineValue}" not found. Please use an existing cuisine.`
        });
      }
    }
  }

  // Spice Level validation (required unless DESSERT)
  // Use the validatedType we determined above, or fallback to typeValue
  const typeForSpiceCheck = validatedType || typeValue || row.type || '';
  if (typeForSpiceCheck && typeForSpiceCheck.toUpperCase().trim() !== 'DESSERT') {
    if (!row.spiceLevel || isNaN(Number(row.spiceLevel))) {
      errors.push({
        field: 'spiceLevel',
        message: getTranslation('import.importErrorMessages.spiceLevelRequired', language) || 'Spice Level is required (1-4)'
      });
    } else {
      const spiceLevel = Number(row.spiceLevel);
      if (spiceLevel < 1 || spiceLevel > 4) {
        errors.push({
          field: 'spiceLevel',
          message: getTranslation('import.importErrorMessages.invalidSpiceLevel', language) || 'Spice Level must be between 1 and 4'
        });
      }
    }
  }

  // Price validation (optional but validated)
  if (row.price !== undefined && row.price !== null && row.price !== '') {
    const price = Number(row.price);
    if (isNaN(price) || price < 0) {
      errors.push({
        field: 'price',
        message: getTranslation('import.importErrorMessages.invalidPrice', language) || 'Price must be a valid positive number'
      });
    }
  }

  // Optional field validations (warnings)
  if (row.orderBefore !== undefined && row.orderBefore !== null && row.orderBefore !== '') {
    const orderBefore = Number(row.orderBefore);
    if (isNaN(orderBefore) || orderBefore < 0) {
      warnings.push({
        field: 'orderBefore',
        message: getTranslation('import.importWarningMessages.invalidOrderBefore', language) || 'Order Before must be a valid number. Using default value: 1'
      });
    }
  }

  if (row.minimumOrderCount !== undefined && row.minimumOrderCount !== null && row.minimumOrderCount !== '') {
    const minimumOrderCount = Number(row.minimumOrderCount);
    if (isNaN(minimumOrderCount) || minimumOrderCount < 1) {
      warnings.push({
        field: 'minimumOrderCount',
        message: getTranslation('import.importWarningMessages.invalidMinimumOrderCount', language) || 'Minimum Order Count must be at least 1. Using default value: 1'
      });
    }
  }

  // Image validation (warning if missing)
  if (!row.imageUrl && !row.imageFileName) {
    warnings.push({
      field: 'image',
      message: getTranslation('import.importWarningMessages.noImage', language) || 'No image provided. Dish will be created without an image.'
    });
  }

  // Note: Dish names don't need to be unique - only IDs are unique

  return {
    isValid: errors.length === 0,
    errors,
    warnings,
    rowIndex
  };
};

/**
 * Creates optimized lookup maps for fast validation
 * @param {Array} existingDishes - Array of existing dishes (not used for duplicate checking)
 * @param {Array} tags - Array of dish tags
 * @param {Array} cuisines - Array of cuisine tags
 * @returns {Object} Lookup maps for O(1) access
 */
export const createValidationContext = (existingDishes = [], tags = [], cuisines = []) => {
  // Note: Dish names don't need to be unique - only IDs are unique

  // Create Map for tags (check by nameHy, nameEn, nameRu, and translations)
  const tagsMap = new Map();
  tags.forEach(tag => {
    // Add by nameHy
    if (tag.tag) {
      tagsMap.set(tag.tag.toLowerCase().trim(), tag);
    }
    // Add by translations
    if (tag.translations) {
      tag.translations.forEach(translation => {
        if (translation.value) {
          tagsMap.set(translation.value.toLowerCase().trim(), tag);
        }
      });
    }
  });

  // Create Map for cuisines (check by cuisineTag and translations)
  const cuisinesMap = new Map();
  cuisines.forEach(cuisine => {
    // Add by cuisineTag
    if (cuisine.cuisineTag) {
      cuisinesMap.set(cuisine.cuisineTag.toLowerCase().trim(), cuisine);
    }
    // Add by translations
    if (cuisine.translations) {
      cuisine.translations.forEach(translation => {
        if (translation.value) {
          cuisinesMap.set(translation.value.toLowerCase().trim(), cuisine);
        }
      });
    }
  });

  return {
    tagsMap,
    cuisinesMap,
    tags, // Keep original array for fallback lookup
    cuisines // Keep original array for fallback lookup
  };
};

/**
 * Batch validates multiple rows efficiently
 * @param {Array} rows - Array of row data
 * @param {Object} context - Validation context
 * @param {string} language - Current language
 * @param {number} batchSize - Number of rows to process per batch (default: 50)
 * @returns {Promise<Array>} Array of validation results
 */
export const batchValidateRows = async (rows, context, language, batchSize = 50) => {
  const results = [];
  
  // Process in batches to avoid blocking
  for (let i = 0; i < rows.length; i += batchSize) {
    const batch = rows.slice(i, i + batchSize);
    const batchResults = batch.map((row, index) => 
      validateDishRow(row, i + index, context, language)
    );
    results.push(...batchResults);
    
    // Yield to event loop every batch
    if (i + batchSize < rows.length) {
      await new Promise(resolve => setTimeout(resolve, 0));
    }
  }
  
  return results;
};
